/**
 * The wheel gestures over the map, the same in 2D (SketchCanvas.tsx) and 3D (Sketch3D.tsx) (editor/hotkeys.ts, scope
 * "sketch"): Shift + wheel the size (an obstacle, a tree's crown; turns the selected panels), Alt + wheel the kind (the
 * next obstacle variant, the tree type; portrait ⇄ landscape: the selected panels themselves, else the next ones),
 * Shift + Alt + wheel the height. They change the selection, else what the tool places next. Pure: the host is the
 * editor's latest props, updated in place so quick wheel events build on each other before React renders.
 */
import type { ObstacleItem, PanelItem } from "../editor/model.ts";
import type { LiveRoof } from "../editor/roof.ts";
import type { ModuleSize, Orientation } from "../editor/snap.ts";
import type { S } from "../api/client";
import { magnetise, type PlaceMode } from "./gizmo.ts";
import { setHeight, type ShadeItem } from "./shade.ts";
import { metres, type Text } from "./text.ts";
import type { SketchSelection, SketchTool } from "./types.ts";
import { nextObstacle, nextTree, readout, wheelObstacle, wheelOptions, wheelTree, type ShadeOptions, type WheelGesture } from "./variants.ts";

/** What the gestures read and change (the views' props). */
export interface WheelHost {
  tool: SketchTool;
  selection: SketchSelection;
  readOnly: boolean;
  obstacles: ObstacleItem[];
  shade: ShadeItem[];
  shadeOptions: ShadeOptions;
  orientation: Orientation;
  placeMode: PlaceMode;
  panels: PanelItem[];
  roof: LiveRoof;
  setbacks: S["SetbacksOutput"] | null;
  module: ModuleSize;
  gap: number;
  onObstacles: (o: ObstacleItem[], transient?: boolean) => void;
  onShade: (s: ShadeItem[], transient?: boolean) => void;
  onPanels: (p: PanelItem[], transient?: boolean) => void;
  onShadeOptions?: (o: Partial<ShadeOptions>) => void;
  onOrientation?: (o: Orientation) => void;
  onTurnPanels?: (deg: number) => void;
}

export type WheelTarget = "obstacle" | "tree" | "neighbour" | "next-obstacle" | "next-tree" | "flip" | "orientation" | "turn";

/** What a wheel gesture changes now: the selected obstacle or tree (a building or wall: its height), else the next
 * one the tool places, else the panels (Alt: the selected ones flip portrait ⇄ landscape, or the next ones; Shift
 * turns the selected ones). */
export function wheelTarget(p: WheelHost, gesture: WheelGesture): WheelTarget | null {
  const sel = p.selection;
  if (sel?.kind === "obstacle" && p.obstacles.some((o) => o.key === sel.key)) return "obstacle";
  if (sel?.kind === "shade") {
    const t = p.shade.find((x) => x.key === sel.key);
    if (t?.kind === "tree") return "tree";
    if (t && gesture === "height") return "neighbour";
  }
  if (p.tool === "obstacle") return "next-obstacle";
  if (p.tool === "tree") return "next-tree";
  if (gesture === "kind" && sel?.kind === "panels" && sel.keys.length) return "flip";
  if (gesture === "kind" && p.tool === "panel") return "orientation";
  if (gesture === "size" && sel?.kind === "panels" && p.onTurnPanels) return "turn";
  return null;
}

/** The selected panels turned portrait ⇄ landscape about their centres (magnetic: snapped back onto the grid). */
export function flipPanels(p: WheelHost, keys: Set<string>): PanelItem[] {
  const flipped = p.panels.map((x) => (keys.has(x.key) ? { ...x, orientation: (x.orientation === "portrait" ? "landscape" : "portrait") as Orientation } : x));
  return p.placeMode === "magnetic" ? magnetise({ roof: p.roof, panels: flipped, obstacles: p.obstacles, setbacks: p.setbacks, module: p.module, gap: p.gap }, keys) : flipped;
}

/** Applies `steps` of a gesture (transient: a burst is one undo step) and says what changed (in `tx`'s language). */
export function applyWheel(p: WheelHost, gesture: WheelGesture, steps: number, tx: Text): string {
  const sel = p.selection;
  switch (wheelTarget(p, gesture)) {
    case "obstacle": {
      let made: ObstacleItem | null = null;
      const list = p.obstacles.map((o) => (sel?.kind === "obstacle" && o.key === sel.key ? (made = wheelObstacle(o, gesture, steps)) : o));
      p.obstacles = list;
      p.onObstacles(list, true);
      return made ? readout(tx, { obstacle: made }, gesture) : "";
    }
    case "tree": {
      let made: ShadeItem | null = null;
      const list = p.shade.map((t) => (sel?.kind === "shade" && t.key === sel.key ? (made = wheelTree(t, gesture, steps)) : t));
      p.shade = list;
      p.onShade(list, true);
      return made ? readout(tx, { tree: made }, gesture) : "";
    }
    case "neighbour": {
      let h = 0;
      const list = p.shade.map((t) => (sel?.kind === "shade" && t.key === sel.key ? ((h = Math.max(0.5, Math.round((t.height + 0.5 * steps) * 10) / 10)), setHeight(t, h)) : t));
      p.shade = list;
      p.onShade(list, true);
      return tx.t("sketch.readout.high", { height: metres(tx, h) });
    }
    case "next-obstacle":
    case "next-tree": {
      const tool = p.tool === "obstacle" ? "obstacle" : "tree";
      const opts = wheelOptions(p.shadeOptions, tool, gesture, steps);
      p.shadeOptions = opts;
      p.onShadeOptions?.(opts);
      const text = tool === "obstacle" ? readout(tx, { obstacle: nextObstacle(opts, [0, 0]) }, gesture) : readout(tx, { tree: nextTree(opts, [0, 0]) }, gesture);
      return tx.t("sketch.readout.next", { text });
    }
    case "flip": {
      if (sel?.kind !== "panels" || Math.abs(steps) % 2 === 0) return "";
      const keys = new Set(sel.keys);
      const list = flipPanels(p, keys);
      p.panels = list;
      p.onPanels(list, true);
      const first = list.find((x) => keys.has(x.key));
      const text = readout(tx, { panels: { orientation: first?.orientation ?? "portrait" } }, "kind");
      return keys.size > 1 ? tx.t("sketch.readout.panels", { text, count: keys.size }) : text;
    }
    case "orientation": {
      if (Math.abs(steps) % 2 === 0) return "";
      const o: Orientation = p.orientation === "portrait" ? "landscape" : "portrait";
      p.orientation = o;
      p.onOrientation?.(o);
      return tx.t("sketch.readout.next", { text: readout(tx, { panels: { orientation: o } }, "kind") });
    }
    case "turn": {
      p.onTurnPanels?.(15 * steps);
      return readout(tx, { panels: { orientation: p.orientation, turned: 15 * steps } }, "size");
    }
    default:
      return "";
  }
}
